import { Component, OnInit, DoCheck, forwardRef, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FirebaseCountryObject } from '@app/profile-setup/profile.model';
import { CommonService } from '@app/shared/services/common.service';
import { IonIntlTelInputModel } from './ion-intl-tel-input.model';
import { IonicSelectableComponent } from 'ionic-selectable';
interface CountryOption {
  isoCode: string;
  name: string;
  dialCode: string;
  label: string;
}

@Component({
  selector: 'ion-intl-tel-input',
  templateUrl: './ion-intl-tel-input.component.html',
  styleUrls: ['./ion-intl-tel-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IonIntlTelInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class IonIntlTelInputComponent implements ControlValueAccessor, OnInit, DoCheck {
  @Input() public defaultCountryiso = 'in';
  @Input() public modalCssClass?: string;
  @Input() public inputPlaceholder?: string;
  @Input() public modalCloseText?: string;
  @Input() public allowDecimals = true;
  @Input() public allowSign = false;
  @Input() public maxDigits = 15;

  public disabled = false;
  public displayValue = '';
  public currentDialCode = '+91';
  public selectedCountry: CountryOption | null = null;
  public readonly countries: CountryOption[] = [];

  private onChange: (value: IonIntlTelInputModel | string) => void = () => {};
  private onTouched: () => void = () => {};
  @ViewChild('selectComponent') public selectComponent!: IonicSelectableComponent;
  constructor(private commonService: CommonService) {}


  public openCountrySelector() {
  this.selectComponent.open();
  setTimeout(() => {
    const titleEl = document.querySelector('ionic-selectable-modal ion-title');
    if (titleEl) {
      titleEl.textContent = 'Select Country';
    }
  }, 100);
}

  private countriesSignature = '';
  public ngDoCheck(): void {
    this.refreshCountriesIfChanged();
  }

  public ngOnInit(): void {
    this.loadCountries();
    this.selectByIso(this.normalizeIso(this.defaultCountryiso));
    this.refreshCountriesIfChanged();
  }

  public writeValue(value: IonIntlTelInputModel | string | null): void {
    this.loadCountries();

    if (value === undefined) {
      return;
    }

    const defaultIso = this.normalizeIso(this.defaultCountryiso);

    if (!value) {
      this.displayValue = '';
      if (!this.selectedCountry) {
        this.selectByIso(defaultIso);
      } else {
        this.currentDialCode = this.selectedCountry.dialCode;
      }
      return;
    }

    if (typeof value === 'string') {
      const parsed = this.parsePhoneInput(value);
      this.displayValue = this.limitDigits(parsed.nationalNumber);

      if (parsed.dialCode) {
        const fromDial = this.findByDialCode(parsed.dialCode);
        if (fromDial) {
          this.selectedCountry = fromDial;
          this.currentDialCode = fromDial.dialCode;
        } else {
          this.currentDialCode = parsed.dialCode;
        }
      } else if (!this.selectedCountry) {
        this.selectByIso(defaultIso);
      }
      return;
    }

    const incomingIso = this.normalizeIso(value.isoCode || value.countryCode || defaultIso);
    this.selectByIso(incomingIso);

    if (value.dialCode) {
      const normalizedDial = this.normalizeDialCode(value.dialCode);
      const byDial = this.findByDialCode(normalizedDial);
      if (byDial) {
        this.selectedCountry = byDial;
        this.currentDialCode = byDial.dialCode;
      } else {
        this.currentDialCode = normalizedDial || this.currentDialCode;
      }
    }

      const parsedValue = this.parsePhoneInput(
        value.internationalNumber || value.e164Number || value.nationalNumber || ''
      );
      this.displayValue = this.limitDigits(value.nationalNumber || parsedValue.nationalNumber || '');
  }

  public registerOnChange(fn: (value: IonIntlTelInputModel | string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onInput(event: Event): void {
    const raw = (event as CustomEvent).detail?.value ?? '';
    const parsed = this.parsePhoneInput(String(raw));

    if (parsed.dialCode) {
      const country = this.findByDialCode(parsed.dialCode);
      if (country) {
        this.selectedCountry = country;
        this.currentDialCode = country.dialCode;
      } else {
        this.currentDialCode = parsed.dialCode;
      }
    }

    this.displayValue = this.limitDigits(parsed.nationalNumber);
    this.onChange(this.toModel(this.displayValue));
  }

  public onCountryChange(event: Event): void {
    const detail = (event as CustomEvent).detail || {};
    const country = (detail.value || detail.item || this.selectedCountry) as CountryOption | null;

    if (country) {
      this.selectedCountry = country;
      this.currentDialCode = country.dialCode;
    }

    this.onChange(this.toModel(this.displayValue));
  }

  private refreshCountriesIfChanged(): void {
    const source = this.commonService?.countryCode;
    const list = Array.isArray(source) ? source : Object.values(source || {});
    const signature = `${list.length}|${list[0]?.countryCode || ''}|${list[1]?.countryCode || ''}`;

    if (signature === this.countriesSignature) {
      return;
    }

    this.countriesSignature = signature;
    this.loadCountries();
  }

  private toModel(value: string): IonIntlTelInputModel {
    const digits = this.limitDigits(value);
    const selected = this.selectedCountry || this.getDefaultCountry();
    const dialCode = selected?.dialCode || this.currentDialCode || '+91';
    const isoCode = selected?.isoCode || this.normalizeIso(this.defaultCountryiso);

    return {
      internationalNumber: digits ? `${dialCode} ${digits}` : '',
      nationalNumber: digits,
      e164Number: digits ? `${dialCode}${digits}` : '',
      dialCode,
      countryCode: isoCode,
      isoCode,
    };
  }

  private loadCountries(): void {
    const source = this.commonService?.countryCode;
    const list = Array.isArray(source) ? source : Object.values(source || {});
    const mapped = list
      .map((country: FirebaseCountryObject) => this.mapCountry(country))
      .filter((country: CountryOption | null): country is CountryOption => Boolean(country));

    if (!mapped.length) {
      if (!this.countries.length) {
        this.countries.push({ isoCode: 'in', name: 'India', dialCode: '+91', label: 'India +91' });
      }
      return;
    }

    this.countries.length = 0;
    this.countries.push(...mapped.sort((a, b) => a.name.localeCompare(b.name)));

    if (this.selectedCountry) {
      const refreshed = this.findByIso(this.selectedCountry.isoCode);
      if (refreshed) {
        this.selectedCountry = refreshed;
        this.currentDialCode = refreshed.dialCode;
      }
    } else {
      this.selectByIso(this.normalizeIso(this.defaultCountryiso));
    }
  }


  private extractDialCode(country: Partial<FirebaseCountryObject> & { [key: string]: any }): string {
    const raw = country?.callingCodes ?? country?.dialCode ?? country?.callingCode ?? country?.phoneCode ?? '';

    if (Array.isArray(raw)) {
      return this.normalizeDialCode(raw[0]);
    }

    if (raw && typeof raw === 'object') {
      const firstValue = Object.values(raw)[0];
      return this.normalizeDialCode(firstValue as string);
    }

    const text = String(raw || '').trim();
    if (!text) {
      return '';
    }

    // Handles formats like "+1,+91" or "1, 91" by taking the first code
    const first = text.split(',')[0]?.trim() || text;
    return this.normalizeDialCode(first);
  }

  private mapCountry(country: FirebaseCountryObject): CountryOption | null {
    const isoCode = this.normalizeIso(country?.countryCode);
    const name = String(country?.name || '').trim();
    const dialCode = this.extractDialCode(country);

    if (!isoCode || !name || !dialCode) {
      return null;
    }

    return { isoCode, name, dialCode, label: `${name} ${dialCode}` };
  }

  private normalizeIso(value?: string): string {
    return String(value || 'in').toLowerCase().replace(/[^a-z]/g, '').slice(0, 2) || 'in';
  }

  private normalizeDialCode(value?: string): string {
    const raw = String(value || '').trim();
    if (!raw) {
      return '';
    }
    if (raw.startsWith('+')) {
      return raw.replace(/[^\d+]/g, '');
    }
    const digits = raw.replace(/\D/g, '');
    return digits ? `+${digits}` : '';
  }

  private parsePhoneInput(value: string): { dialCode: string; nationalNumber: string } {
    const input = String(value || '').trim();
    const withDialMatch = input.match(/^\+(\d[\d\s]*)\s*(.*)$/);
    if (withDialMatch) {
      return {
        dialCode: `+${withDialMatch[1].replace(/\s+/g, '')}`,
        nationalNumber: String(withDialMatch[2] || '').replace(/\D/g, ''),
      };
    }
    return { dialCode: '', nationalNumber: input.replace(/\D/g, '') };
  }

  private limitDigits(value: string): string {
    return String(value || '')
      .replace(/\D/g, '')
      .slice(0, Math.max(0, Number(this.maxDigits) || 15));
  }

  private findByIso(iso: string): CountryOption | null {
    const normalized = this.normalizeIso(iso);
    return this.countries.find((country) => country.isoCode === normalized) || null;
  }

  private findByDialCode(dialCode: string): CountryOption | null {
    const normalized = this.normalizeDialCode(dialCode);
    return this.countries.find((country) => country.dialCode === normalized) || null;
  }

  private getDefaultCountry(): CountryOption | null {
    return this.findByIso(this.defaultCountryiso) || this.findByIso('in');
  }

  private selectByIso(iso: string): void {
    const selected = this.findByIso(iso) || this.getDefaultCountry();
    if (selected) {
      this.selectedCountry = selected;
      this.currentDialCode = selected.dialCode;
    }
  }
}
